import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getCollectionBySlug } from "@/services/collections";
import { getMessagesByCollection, subscribeToMessages, unsubscribeFromMessages } from "@/services/messages";
import { getContributionsByCollection } from "@/services/contributions";
import { getRsvpsByCollection, createRsvp } from "@/services/rsvps";
import { supabase } from "@/integrations/supabase/client";
import { isUserInvited, getInvitedGuests } from "@/services/invited-guests";
import { getTemplateImageUrl } from "@/services/templates";
import { logClientError } from "@/services/error-logs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Check, X, Users, Search, Heart, PackageCheck, Palette, ExternalLink, ThumbsDown, MapPin, CalendarDays, UserPlus, PartyPopper, Sparkles, CalendarPlus, HelpCircle, ShoppingBag } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import ContributeWizard from "@/components/card/ContributeWizard";
import AuthBottomSheet from "@/components/card/AuthBottomSheet";
import InteractiveCard from "@/components/card/InteractiveCard";
import InviteModal from "@/components/card/InviteModal";
import GiftSuggestions from "@/components/card/GiftSuggestions";
import SignersBubbles, { SignerDetail, BubbleMedia, colorForName, getInitials } from "@/components/card/SignersBubbles";
import StoriesViewer from "@/components/card/StoriesViewer";
import SignersStoriesBar from "@/components/card/SignersStoriesBar";
import CardEnvelope from "@/components/card/CardEnvelope";
import ProductPoolHero from "@/components/card/ProductPoolHero";
import AppHeader from "@/components/AppHeader";
import RsvpPreferenceForm from "@/components/card/RsvpPreferenceForm";
import RsvpCelebration from "@/components/card/RsvpCelebration";
import type { RsvpQuestion } from "@/components/card/RsvpQuestionBuilder";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCurrency } from "@/hooks/useCurrency";
import { ChevronDown, ChevronRight, Gift } from "lucide-react";
import RecipientWishlistsInline from "@/components/wishlist/RecipientWishlistsInline";
import WishlistBottomSheet from "@/components/wishlist/WishlistBottomSheet";

import type { Tables } from "@/integrations/supabase/types";



const CardView = () => {
  const { slug } = useParams();
  
  const location = useLocation();
  const fromDashboard = !!(location.state as { fromDashboard?: boolean })?.fromDashboard;
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const currency = useCurrency();
  
  const [collection, setCollection] = useState<Tables<"collections"> | null>(null);
  const [messages, setMessages] = useState<Tables<"messages">[]>([]);
  const [contributions, setContributions] = useState<Tables<"contributions">[]>([]);
  const [templateUrl, setTemplateUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [wizardMode, setWizardMode] = useState<"sign" | "chipIn" | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);

  const [groupSearch, setGroupSearch] = useState("");

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [rsvps, setRsvps] = useState<any[]>([]);
  const [invitedGuests, setInvitedGuests] = useState<{ id: string; email: string }[]>([]);
  
  const [rsvpForm, setRsvpForm] = useState({ name: "", email: "", plusOne: false, plusOneName: "" });
  const [rsvpAnswers, setRsvpAnswers] = useState<Record<string, string | string[]>>({});
  const [rsvpStep, setRsvpStep] = useState<"choose" | "preferences">("choose");
  const [submittingRsvp, setSubmittingRsvp] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [existingRsvp, setExistingRsvp] = useState<any>(null);
  const [rsvpCelebrationResponse, setRsvpCelebrationResponse] = useState<"yes" | "no" | "waitlist" | null>(null);
  const [showStories, setShowStories] = useState(false);
  const [storyStartIndex, setStoryStartIndex] = useState(0);
  const [seenStoryKeys, setSeenStoryKeys] = useState<Set<string>>(new Set());
  const [selectedGroupMemberId, setSelectedGroupMemberId] = useState<string | null>(null);
  const [organizerProfile, setOrganizerProfile] = useState<{ display_name: string | null; email: string | null } | null>(null);
  
  const [showAuthSheet, setShowAuthSheet] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [cardScrolledPast, setCardScrolledPast] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [capacityReached, setCapacityReached] = useState(false);
  const [draftCard, setDraftCard] = useState<{ title: string; occasion: string } | null>(null);
  // Tracks whether a collection has ever loaded successfully. Used in the
  // fetch effect to demote fetch failures on re-runs from fatal (blank
  // error screen) to silent — the user's already-loaded view shouldn't be
  // destroyed by a transient session refresh.
  const hadCollectionRef = useRef(false);

  const isActualOrganizer = user && collection && user.id === collection.organizer_id;
  // Product pools take over the hero with a product showcase instead
  // of the gift-card cover, and the sign-only CTA is redundant since
  // the chip-in wizard already collects a message alongside the
  // contribution. Computed here so the render can branch on it in
  // all 3 button locations (sticky footer, mobile inline, desktop).
  const isProductPool =
    (collection as { pool_type?: string } | null)?.pool_type === "product";
  const requiresLogin = (collection as any)?.require_login && !user;
  const rsvpYesResponse = capacityReached ? "waitlist" : "yes";
  const rsvpYesLabel = capacityReached
    ? t("cardView.joinWaitlist", { defaultValue: "Join Waitlist" })
    : t("cardView.illBeThere");

  const handleAuthGatedAction = (action: () => void) => {
    if (requiresLogin) {
      setPendingAction(() => action);
      setShowAuthSheet(true);
    } else {
      action();
    }
  };


  // Track when user scrolls past the card on mobile
  useEffect(() => {
    if (!collection) return;
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;
    
    const handleScroll = () => {
      // Card + buttons area is roughly 550px tall from the top of the content
      setCardScrolledPast(window.scrollY > 500);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [collection]);

  useEffect(() => {
    let channel: ReturnType<typeof subscribeToMessages> | null = null;
    // Track whether this effect run has been superseded. When slug/user/
    // authLoading changes mid-fetch, the old run is canceled and its
    // state updates must be dropped so a stale failure can't overwrite a
    // fresh success (or flip an already-loaded card into the error screen).
    let canceled = false;
    // If we've already loaded a collection in a previous effect run,
    // subsequent re-fetches are best-effort refreshes — a failure on
    // those should NOT flip the page into "Something went wrong".
    const hadCollection = hadCollectionRef.current;

    // Don't fetch until auth state is resolved
    if (authLoading) return;

    const fetchCollectionWithRetry = async (): Promise<{ data: Tables<"collections"> | null; error: unknown } | null> => {
      const maxAttempts = 3;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (canceled) return null;
        try {
          const result = await getCollectionBySlug(slug!);
          if (!result.error) return result;
          if (attempt === maxAttempts - 1) {
            console.error("[CardView] collection fetch failed after retries:", result.error);
            return result;
          }
          const delay = 500 * Math.pow(2, attempt);
          console.warn(`[CardView] collection fetch attempt ${attempt + 1} failed, retrying in ${delay}ms`, result.error);
          await new Promise((r) => setTimeout(r, delay));
        } catch (err) {
          if (attempt === maxAttempts - 1) {
            console.error("[CardView] collection fetch threw after retries:", err);
            return { data: null, error: err };
          }
          const delay = 500 * Math.pow(2, attempt);
          console.warn(`[CardView] collection fetch attempt ${attempt + 1} threw, retrying in ${delay}ms`, err);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      return null;
    };

    const settled = <T,>(p: Promise<T>, label: string, fallback: T): Promise<T> =>
      p.catch((err) => {
        console.warn(`[CardView] ${label} failed:`, err);
        logClientError({
          type: "card_secondary_query_failed",
          message: `${label} failed`,
          context: {
            slug,
            query: label,
            error: err instanceof Error ? { name: err.name, message: err.message } : err,
          },
        });
        return fallback;
      });

    const fetchCard = async () => {
      const colResult = await fetchCollectionWithRetry();
      if (canceled) return;

      // Only the primary collection fetch is critical. If it ultimately
      // fails we show the error screen; every secondary query below is
      // allowed to fail independently without taking down the page.
      if (!colResult || colResult.error) {
        // Never flip an already-loaded card into the full-page error —
        // the user is mid-interaction and a transient refresh failure
        // shouldn't blow away their view. Silently keep showing the
        // cached collection; realtime will heal when the backend is back.
        if (!hadCollection) {
          logClientError({
            type: "card_fetch_failed",
            message: "Initial getCollectionBySlug failed after retries",
            context: {
              slug,
              error: colResult?.error instanceof Error
                ? { name: colResult.error.name, message: colResult.error.message }
                : colResult?.error ?? null,
            },
          });
          setFetchError(true);
          setLoading(false);
        } else {
          logClientError({
            type: "card_refetch_failed",
            message: "Re-fetch of loaded card failed; keeping cached view",
            context: {
              slug,
              error: colResult?.error instanceof Error
                ? { name: colResult.error.name, message: colResult.error.message }
                : colResult?.error ?? null,
            },
          });
        }
        return;
      }
      const col = colResult.data;

      if (!col) {
        // Collection not visible — check if it exists as a draft
        try {
          const { data: statusData } = await supabase.rpc("check_card_status", { card_slug: slug! } as any);
          if (canceled) return;
          const parsed = statusData as any;
          if (parsed && parsed.exists && parsed.status === "draft") {
            setDraftCard({ title: parsed.title, occasion: parsed.occasion });
          }
        } catch (err) {
          console.warn("[CardView] check_card_status failed:", err);
        }
        if (!canceled) setLoading(false);
        return;
      }
      if (canceled) return;
      setCollection(col);
      hadCollectionRef.current = true;

      // Check invite-only access — organizers always bypass
      const isInviteOnly = !!(col as any).invite_only;
      const isOrganizer = user && user.id === col.organizer_id;

      // Reset access denied on each fetch
      setAccessDenied(false);

      if (isInviteOnly && !isOrganizer) {
        if (!user?.email) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        try {
          const { invited } = await isUserInvited(col.id, user.email);
          if (canceled) return;
          if (!invited) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn("[CardView] isUserInvited failed:", err);
          if (!canceled) {
            setAccessDenied(true);
            setLoading(false);
          }
          return;
        }
      }

      const [templateResult, messagesResult, contributionsResult, rsvpsResult, invitedResult] = await Promise.all([
        col.card_template_id
          ? settled(getTemplateImageUrl(col.card_template_id), "getTemplateImageUrl", { data: null as any, error: null })
          : Promise.resolve(null),
        settled(getMessagesByCollection(col.id), "getMessagesByCollection", { data: [] as Tables<"messages">[], error: null }),
        settled(getContributionsByCollection(col.id), "getContributionsByCollection", { data: [] as Tables<"contributions">[], error: null }),
        settled(getRsvpsByCollection(col.id), "getRsvpsByCollection", { data: [] as any[], error: null }),
        settled(getInvitedGuests(col.id), "getInvitedGuests", { data: [] as any[], error: null }),
      ]);
      if (canceled) return;

      setTemplateUrl(col.custom_cover_url || templateResult?.data?.image_url || "");
      setMessages(messagesResult.data || []);
      if (contributionsResult.data) setContributions(contributionsResult.data);
      setRsvps(rsvpsResult.data || []);
      setInvitedGuests((invitedResult.data || []).map((g: any) => ({ id: g.id, email: g.email })));

      // Check capacity
      const maxCap = (col as any).max_capacity;
      if (maxCap && (rsvpsResult.data || []).filter((r: any) => r.response === "yes").length >= maxCap) {
        setCapacityReached(true);
      }

      // Fetch organizer profile — non-critical
      try {
        const { data: orgProfile } = await supabase
          .from("profiles")
          .select("display_name, email")
          .eq("user_id", col.organizer_id)
          .maybeSingle();
        if (!canceled && orgProfile) setOrganizerProfile(orgProfile);
      } catch (err) {
        console.warn("[CardView] organizer profile fetch failed:", err);
      }

      // Fetch seen story keys for current user — non-critical
      if (user) {
        try {
          const { data: seenRows } = await supabase
            .from("story_views")
            .select("contributor_key")
            .eq("user_id", user.id)
            .eq("collection_id", col.id);
          if (!canceled && seenRows) {
            setSeenStoryKeys(new Set(seenRows.map((r: any) => r.contributor_key)));
          }
        } catch (err) {
          console.warn("[CardView] story_views fetch failed:", err);
        }
      }
      // Check if logged-in user already RSVP'd
      if (!canceled && user?.email && col.rsvp_enabled) {
        const existing = (rsvpsResult.data || []).find(
          (r: any) => r.guest_email?.toLowerCase() === user.email?.toLowerCase()
        );
        if (existing) {
          setExistingRsvp(existing);
          setRsvpSubmitted(true);
        }
      }

      if (canceled) return;
      setLoading(false);
      // A successful re-fetch clears any previous error state so the
      // user sees fresh data if they recover from a transient failure.
      setFetchError(false);

      // Subscribe using collection ID (filtered to this collection only)
      try {
        channel = subscribeToMessages(col.id, (newMessage) => {
          setMessages((prev) => [...prev, newMessage as Tables<"messages">]);
        });
      } catch (err) {
        console.warn("[CardView] subscribeToMessages failed:", err);
      }
    };
    fetchCard();

    return () => {
      canceled = true;
      if (channel) unsubscribeFromMessages(channel);
    };
  }, [slug, user, authLoading]);

  // Safety net: whenever the full-page error screen is actually shown
  // (fetchError flipped from false to true) capture the event unconditionally.
  // If the main fetch path's log call is ever missed — stale closure, swallowed
  // promise, whatever — this guarantees the admin Errors tab has a record.
  useEffect(() => {
    if (fetchError) {
      logClientError({
        type: "card_error_screen_shown",
        message: "fetchError UI rendered",
        context: {
          slug,
          hadCollection: hadCollectionRef.current,
        },
      });
    }
  }, [fetchError, slug]);

  // Re-derive templateUrl when collection design changes
  useEffect(() => {
    if (!collection) return;
    const resolve = async () => {
      if (collection.custom_cover_url) {
        setTemplateUrl(collection.custom_cover_url);
      } else if (collection.card_template_id) {
        const result = await getTemplateImageUrl(collection.card_template_id);
        setTemplateUrl(result?.data?.image_url || "");
      } else {
        setTemplateUrl("");
      }
    };
    resolve();
  }, [collection?.custom_cover_url, collection?.card_template_id]);



  // Re-check RSVP status when user auth loads after initial fetch
  useEffect(() => {
    if (user?.email && collection && (collection as any).rsvp_enabled && rsvps.length > 0 && !rsvpSubmitted) {
      const existing = rsvps.find(
        (r: any) => r.guest_email?.toLowerCase() === user.email?.toLowerCase()
      );
      if (existing) {
        setExistingRsvp(existing);
        setRsvpSubmitted(true);
      }
    }
  }, [user, rsvps, collection]);

  const rsvpQuestions: RsvpQuestion[] = Array.isArray((collection as any)?.rsvp_questions)
    ? ((collection as any).rsvp_questions as RsvpQuestion[]).filter((question) => question?.question?.trim())
    : [];

  const scrollToRsvpForm = () => {
    requestAnimationFrame(() => {
      document.getElementById("rsvp-form-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const promptYesRsvpFlow = () => {
    const canUseAuthenticatedIdentity = Boolean((collection as any)?.require_login && user);
    const needsIdentityStep = !canUseAuthenticatedIdentity && !rsvpForm.name.trim();
    const needsPlusOneStep = Boolean((collection as any)?.rsvp_plus_one);
    const nextStep = rsvpQuestions.length > 0 && !needsIdentityStep && !needsPlusOneStep ? "preferences" : "choose";

    setRsvpSubmitted(false);
    setRsvpAnswers(((existingRsvp?.rsvp_answers as Record<string, string | string[]>) ?? {}));
    setRsvpStep(nextStep);
    scrollToRsvpForm();
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="w-full max-w-md mx-auto px-4 pt-6 space-y-4 animate-pulse">
          {/* Card skeleton */}
          <div className="w-full aspect-[3/4] rounded-2xl bg-muted/60" />
          {/* Title skeleton */}
          <div className="flex flex-col items-center gap-2 pt-2">
            <div className="h-3 w-20 rounded-full bg-muted/50" />
            <div className="h-6 w-48 rounded-full bg-muted/60" />
            <div className="h-3 w-32 rounded-full bg-muted/40" />
          </div>
          {/* Signers skeleton */}
          <div className="flex justify-center gap-[-4px] pt-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-9 h-9 rounded-full bg-muted/50 -ml-1 first:ml-0" />
            ))}
          </div>
          {/* Actions skeleton */}
          <div className="flex gap-3 pt-2">
            <div className="h-11 flex-1 rounded-full bg-muted/50" />
            <div className="h-11 flex-1 rounded-full bg-muted/40" />
          </div>
        </div>
      </div>
    );
  }
  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.somethingWrong")}</h1>
          <p className="text-muted-foreground mb-4">{t("cardView.couldntLoad")}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            {t("cardView.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (accessDenied && collection) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader useAuthSheet />
        <div className="flex flex-col items-center justify-center px-6 py-20 text-center max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Users size={28} className="text-primary" />
          </div>
          <p className="text-[10px] font-light uppercase tracking-[0.35em] text-muted-foreground mb-1">
            {collection.occasion}
          </p>
          <h2 className="text-2xl font-bold text-foreground font-serif mb-2">
            {collection.title}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t("cardView.inviteOnlyMessage", { defaultValue: "This is an invite-only event. Please sign in with an invited email to view details." })}
          </p>
          {!user ? (
            <Button
              className="rounded-full"
              onClick={() => {
                setPendingAction(() => () => window.location.reload());
                setShowAuthSheet(true);
              }}
            >
              {t("nav.signIn")}
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("cardView.notOnGuestList", { defaultValue: "Your email is not on the guest list. Contact the organizer for access." })}
            </p>
          )}
          <AnimatePresence>
            {showAuthSheet && (
              <AuthBottomSheet
                onClose={() => setShowAuthSheet(false)}
                onAuthenticated={() => { setShowAuthSheet(false); window.location.reload(); }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }
  if (!collection) {
    if (draftCard) {
      return (
        <div className="min-h-screen bg-background">
          <AppHeader useAuthSheet />
          <div className="flex flex-col items-center justify-center px-6 py-20 text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center mb-6">
              <CalendarDays size={28} className="text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-[10px] font-light uppercase tracking-[0.35em] text-muted-foreground mb-1">
              {draftCard.occasion?.replace(/_/g, " ")}
            </p>
            <h2 className="text-2xl font-bold text-foreground font-serif mb-2">
              {draftCard.title}
            </h2>
            <p className="text-sm text-muted-foreground mb-1">
              {t("cardView.notPublished", { defaultValue: "This card hasn't been published yet." })}
            </p>
            <p className="text-xs text-muted-foreground/70">
              {t("cardView.notPublishedDesc", { defaultValue: "The organizer is still setting things up. Check back soon!" })}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.notFound")}</h1>
          <p className="text-muted-foreground">{t("cardView.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  {/* Require login gate — if require_login is on and user is not logged in, show auth prompt */}
  if ((collection as any).require_login && !user) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader useAuthSheet />
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center max-w-md mx-auto">
          {/* Card in envelope */}
          <div className="w-full max-w-[340px] mb-8">
            <CardEnvelope
              templateUrl={templateUrl}
              title={collection.title}
              occasion={collection.occasion}
              tint={(collection as any).envelope_tint || undefined}
            />
          </div>

          <p className="text-[10px] font-light uppercase tracking-[0.35em] text-muted-foreground mb-1">
            {collection.occasion}
          </p>
          <h2 className="text-2xl font-bold text-foreground font-serif mb-2">
            {collection.title}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t("cardView.loginRequired", { defaultValue: "Please sign in to view this card and participate." })}
          </p>
          <Button
            className="rounded-full"
            onClick={() => {
              setPendingAction(() => () => window.location.reload());
              setShowAuthSheet(true);
            }}
          >
            {t("nav.signIn")}
          </Button>
          <AnimatePresence>
            {showAuthSheet && (
              <AuthBottomSheet
                onClose={() => setShowAuthSheet(false)}
                onAuthenticated={() => { setShowAuthSheet(false); window.location.reload(); }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Subtle decorative background pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.4]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.07) 1px, transparent 0)`,
          backgroundSize: '28px 28px',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 0% 0%, hsl(var(--primary) / 0.04), transparent),
            radial-gradient(ellipse 50% 40% at 100% 100%, hsl(var(--primary) / 0.03), transparent)
          `,
        }}
      />

      {/* Fixed header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <AppHeader onBack={fromDashboard ? "/dashboard" : undefined} useAuthSheet userMenuVariant="dashboard" />
      </div>

      {/* Fixed bottom action bar — appears when card scrolls out.
          Suppressed entirely for product pools: their chip-in CTA
          now lives inside the chip-in progress card, and the sign
          CTA doesn't exist for this pool type, so there's nothing
          to surface here. */}
      <div
        className={`lg:hidden fixed left-0 right-0 bottom-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-all duration-300 ${
          isProductPool
            ? "translate-y-full opacity-0 pointer-events-none"
            : cardScrolledPast
              ? "translate-y-0 opacity-100"
              : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-2 w-full max-w-md mx-auto">
          <div className={`grid gap-3 flex-1 min-w-0 ${collection.collection_enabled && !isProductPool ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {collection.collection_enabled && !isProductPool && (
              <Button
                onClick={() => handleAuthGatedAction(() => setWizardMode("chipIn"))}
                className="rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                size="lg"
              >
                {t("cardView.chipIn", { defaultValue: "Chip in" })}
              </Button>
            )}
            {!isProductPool && (
              <Button
                onClick={() => handleAuthGatedAction(() => setWizardMode("sign"))}
                className={
                  collection.collection_enabled
                    ? "rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 h-12 text-base"
                    : "rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                }
                size="lg"
                variant={collection.collection_enabled ? "outline" : "default"}
              >
                {t("cardView.sendAWish", { defaultValue: "Sign the Card" })}
              </Button>
            )}
          </div>
          {isActualOrganizer && !isProductPool && (
            <button
              onClick={() => setShowInviteModal(true)}
              aria-label={t("cardView.inviteAction")}
              className="shrink-0 h-12 w-12 rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 transition-colors flex items-center justify-center"
            >
              <UserPlus size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="relative pt-[57px]">

      {/* Stories bar — who signed */}
      {messages.length > 0 && (
        <div className="w-full max-w-6xl mx-auto border-b border-border/50">
          <SignersStoriesBar
            messages={messages}
            contributions={contributions}
            seenKeys={seenStoryKeys}
            onSignerClick={(key) => {
              // Find first message index for this signer, +1 because story 0 is intro
              const msgIdx = messages.findIndex(m => (m.contributor_email || m.contributor_name) === key);
              setStoryStartIndex(msgIdx >= 0 ? msgIdx + 1 : 0);
              setShowStories(true);
            }}
            onAddClick={() => handleAuthGatedAction(() => setWizardMode("sign"))}
          />
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-2 pb-24 lg:pb-6">
        <div className="flex flex-col lg:flex-row-reverse lg:items-start lg:gap-6 lg:justify-center w-full pt-2 pb-6 lg:py-10">
          {/* Sticky product/card image — placed visually on the far
              left for product pools via lg:order-2 (higher order under
              lg:flex-row-reverse pushes it earlier in the visual row). */}
          <div className={`lg:sticky lg:top-6 shrink-0 flex flex-col items-center w-full lg:w-[560px] mb-6 lg:mb-0 lg:h-fit ${isProductPool ? 'lg:order-2' : ''}`}>
            <div
              className={`w-full ${isProductPool ? 'max-w-none' : 'max-w-[min(280px,calc((100dvh-300px)*500/720))]'} lg:max-w-[440px] transition-all duration-500 ease-in-out lg:opacity-100 lg:translate-y-0 ${
                cardScrolledPast
                  ? "opacity-0 pointer-events-none lg:opacity-100 lg:pointer-events-auto"
                  : "opacity-100 translate-y-0"
              }`}
            >
                {isProductPool ? (
                  <ProductPoolHero collection={collection as any} templateUrl={templateUrl} t={t} />
                ) : (
                  <InteractiveCard templateUrl={templateUrl} title={collection.title} messages={messages} contributions={contributions} envelopeTint={(collection as any).envelope_tint || undefined} musicId={(collection as any).music_id || null} />
                )}
                {/* Mobile inline buttons — visible when card is in view */}
                <div className={`lg:hidden flex items-center gap-2 w-full mt-4 transition-opacity duration-300 ${
                  cardScrolledPast ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}>
                  <div className={`grid gap-3 flex-1 min-w-0 ${collection.collection_enabled && !isProductPool ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {collection.collection_enabled && !isProductPool && (
                      <Button
                        onClick={() => handleAuthGatedAction(() => setWizardMode("chipIn"))}
                        className="rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                        size="lg"
                      >
                        {t("cardView.chipIn", { defaultValue: "Chip in" })}
                      </Button>
                    )}
                    {!isProductPool && (
                      <Button
                        onClick={() => handleAuthGatedAction(() => setWizardMode("sign"))}
                        className={
                          collection.collection_enabled
                            ? "rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 h-12 text-base"
                            : "rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                        }
                        size="lg"
                        variant={collection.collection_enabled ? "outline" : "default"}
                      >
                        {t("cardView.sendAWish", { defaultValue: "Sign the Card" })}
                      </Button>
                    )}
                  </div>
                  {isActualOrganizer && !isProductPool && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      aria-label={t("cardView.inviteAction")}
                      className="shrink-0 h-12 w-12 rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 transition-colors flex items-center justify-center"
                    >
                      <UserPlus size={18} />
                    </button>
                  )}
                </div>
            </div>
            {/* Action buttons below card — desktop only (mobile uses sticky bar) */}
            <div className="hidden lg:flex items-center gap-2 w-full max-w-[440px] mt-4">
              <div className={`grid gap-3 flex-1 min-w-0 ${collection.collection_enabled && !isProductPool ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {collection.collection_enabled && !isProductPool && (
                  <Button
                    onClick={() => handleAuthGatedAction(() => setWizardMode("chipIn"))}
                    className="rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                    size="lg"
                  >
                    {t("cardView.chipIn", { defaultValue: "Chip in" })}
                  </Button>
                )}
                {!isProductPool && (
                  <Button
                    onClick={() => handleAuthGatedAction(() => setWizardMode("sign"))}
                    className={
                      collection.collection_enabled
                        ? "rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 h-12 text-base"
                        : "rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                    }
                    size="lg"
                    variant={collection.collection_enabled ? "outline" : "default"}
                  >
                    {t("cardView.sendAWish", { defaultValue: "Sign the Card" })}
                  </Button>
                )}
              </div>
              {isActualOrganizer && !isProductPool && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  aria-label={t("cardView.inviteAction")}
                  className="shrink-0 h-12 w-12 rounded-full bg-card border border-primary/40 text-primary hover:bg-primary/5 transition-colors flex items-center justify-center"
                >
                  <UserPlus size={18} />
                </button>
              )}
            </div>
          </div>



          {/* Details column — placed visually in the middle for product
              pools via lg:order-1 (see the order comment on the image
              column above). */}
          <div className={`flex flex-col items-center lg:items-stretch w-full max-w-md mx-auto lg:mx-0 lg:max-w-none lg:flex-1 space-y-6 ${isProductPool ? 'lg:order-1' : ''}`}>

            {(collection as any).rsvp_enabled ? (
              /* ── INVITATION MODE (RSVP enabled) ── */
              <>

                {/* Elegant invitation card */}
                <div className="w-full bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                  {/* Decorative top border */}
                  <div className="h-[2px]" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.5), hsl(var(--primary)), hsl(var(--primary) / 0.5), transparent)" }} />

                  <div className="px-6 pt-8 pb-6 text-start space-y-5">
                    {/* Invitation header */}
                    <div className="space-y-1">
                      <p className="text-[10px] font-light uppercase tracking-[0.35em] text-muted-foreground">
                        {collection.occasion}
                      </p>
                      <h2 className="text-2xl font-bold text-foreground font-serif leading-snug">
                        {collection.title}
                      </h2>
                      {organizerProfile && (
                        <p className="text-xs text-muted-foreground font-light">
                          {t("cardView.hostedBy", { defaultValue: "Hosted by" })}{" "}
                          <span className="font-medium text-foreground">{organizerProfile.display_name || organizerProfile.email}</span>
                        </p>
                      )}
                    </div>

                    {/* Decorative divider */}
                    <div className="flex items-center gap-3 px-8">
                      <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.2))" }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
                      <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, hsl(var(--primary) / 0.2), transparent)" }} />
                    </div>

                    {/* Event details */}
                    <div className="space-y-3">
                      {(collection as any).rsvp_event_date && (
                      <div className="flex items-center gap-2.5 text-sm text-foreground flex-wrap">
                          <CalendarDays size={15} className="text-primary shrink-0" />
                          <span className="font-medium">
                            {format(new Date((collection as any).rsvp_event_date), "EEEE, d MMMM yyyy")}
                          </span>
                          <button
                            onClick={() => {
                              const title = encodeURIComponent(collection.title || "Event");
                              const location = encodeURIComponent(
                                (collection as any).rsvp_location_name || (collection as any).rsvp_location_address || ""
                              );
                              const startDate = new Date((collection as any).rsvp_event_date!);
                              const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
                              const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
                              const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(startDate)}/${fmt(endDate)}&location=${location}`;
                              window.open(url, "_blank");
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-primary/30 text-[10px] font-medium text-primary hover:bg-primary/10 transition-colors"
                          >
                            <CalendarPlus size={11} />
                            {t("cardView.addToCalendar", { defaultValue: "Add to calendar" })}
                          </button>
                        </div>
                      )}
                      {(collection as any).rsvp_event_date && (
                        <p className="text-sm text-muted-foreground ps-[27px]">
                          {t("cardView.at")} {format(new Date((collection as any).rsvp_event_date), "h:mm a")}
                        </p>
                      )}
                      {(collection as any).rsvp_location_name && (
                      <div className="flex items-center gap-2.5 text-sm text-foreground">
                          <MapPin size={15} className="text-primary shrink-0" />
                          <div>
                            <span className="font-medium">{(collection as any).rsvp_location_name}</span>
                            {(collection as any).rsvp_location_address && (
                              <p className="text-xs text-muted-foreground mt-0.5">{(collection as any).rsvp_location_address}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Capacity indicator */}
                    {(collection as any).max_capacity && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users size={13} className="text-primary shrink-0" />
                        <span>
                          {rsvps.filter((r: any) => r.response === "yes").length} / {(collection as any).max_capacity}{" "}
                          {t("cardView.spotsLabel", { defaultValue: "spots filled" })}
                          {capacityReached && (
                            <span className="text-accent-foreground font-medium ms-1">
                              — {t("cardView.waitlistOpen", { defaultValue: "Waitlist open" })}
                            </span>
                          )}
                        </span>
                      </div>
                    )}


                    {/* RSVP form - hidden for organizer */}
                    {!isActualOrganizer && <div id="rsvp-form-section" />}
                    {!isActualOrganizer && !rsvpSubmitted && (
                      <>
                        <div className="flex items-center gap-3 px-8">
                          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.2))" }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
                          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, hsl(var(--primary) / 0.2), transparent)" }} />
                        </div>

                        {requiresLogin ? (
                          null
                        ) : (collection as any).require_login && user ? (
                          <div className="space-y-3 text-start">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
                              {rsvpStep === "choose" ? t("cardView.willYouAttend") : t("cardView.preferences", { defaultValue: "Your preferences" })}
                            </p>
                            {rsvpStep === "choose" ? (
                              <>
                                <p className="text-sm text-center text-foreground">
                                  {user.user_metadata?.display_name || user.email}
                                </p>
                                {(collection as any).rsvp_plus_one && (
                                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                    <input type="checkbox" checked={rsvpForm.plusOne} onChange={(e) => setRsvpForm((f) => ({ ...f, plusOne: e.target.checked }))} className="rounded border-border" />
                                    <UserPlus size={14} className="text-muted-foreground" />
                                    {t("cardView.bringingPlusOne")}
                                  </label>
                                )}
                                {rsvpForm.plusOne && (
                                  <Input placeholder={t("cardView.guestName")} value={rsvpForm.plusOneName} onChange={(e) => setRsvpForm((f) => ({ ...f, plusOneName: e.target.value }))} className="h-10 text-sm" />
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    className="flex-1 rounded-full h-11 gap-2"
                                    disabled={submittingRsvp}
                                    onClick={() => {
                                      if (rsvpQuestions.length > 0) {
                                        setRsvpStep("preferences");
                                        return;
                                      }

                                      void (async () => {
                                        setSubmittingRsvp(true);
                                        const { error } = await createRsvp({ collection_id: collection.id, guest_name: user.user_metadata?.display_name || user.email || "Guest", guest_email: user.email || undefined, response: rsvpYesResponse, plus_one: rsvpForm.plusOne, plus_one_name: rsvpForm.plusOneName.trim() || undefined, rsvp_answers: {} });
                                        setSubmittingRsvp(false);
                                        if (!error) { setExistingRsvp({ response: rsvpYesResponse }); setRsvpSubmitted(true); setRsvpCelebrationResponse(rsvpYesResponse as "yes" | "waitlist"); }
                                        else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                      })();
                                    }}
                                  >
                                    <Check size={15} /> {rsvpYesLabel}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="rounded-full h-11 px-5"
                                    disabled={submittingRsvp}
                                    onClick={async () => {
                                      setSubmittingRsvp(true);
                                      const { error } = await createRsvp({ collection_id: collection.id, guest_name: user.user_metadata?.display_name || user.email || "Guest", guest_email: user.email || undefined, response: "no" });
                                      setSubmittingRsvp(false);
                                      if (!error) { setExistingRsvp({ response: "no" }); setRsvpSubmitted(true); setRsvpCelebrationResponse("no"); }
                                      else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                    }}
                                  >
                                    {t("cardView.cantMakeIt")}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <RsvpPreferenceForm questions={rsvpQuestions} answers={rsvpAnswers} onChange={setRsvpAnswers} />
                                <div className="flex gap-2">
                                  <Button variant="outline" className="rounded-full h-11 px-5" onClick={() => setRsvpStep("choose")}>
                                    {t("common.back", { defaultValue: "Back" })}
                                  </Button>
                                  <Button
                                    className="flex-1 rounded-full h-11 gap-2"
                                    disabled={submittingRsvp}
                                    onClick={async () => {
                                      setSubmittingRsvp(true);
                                      const { error } = await createRsvp({ collection_id: collection.id, guest_name: user.user_metadata?.display_name || user.email || "Guest", guest_email: user.email || undefined, response: rsvpYesResponse, plus_one: rsvpForm.plusOne, plus_one_name: rsvpForm.plusOneName.trim() || undefined, rsvp_answers: rsvpAnswers });
                                      setSubmittingRsvp(false);
                                      if (!error) { setExistingRsvp({ response: rsvpYesResponse }); setRsvpSubmitted(true); setRsvpCelebrationResponse(rsvpYesResponse as "yes" | "waitlist"); }
                                      else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                    }}
                                  >
                                    <Check size={15} /> {t("cardView.confirmRsvp", { defaultValue: "Confirm RSVP" })}
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3 text-start">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
                              {rsvpStep === "choose" ? t("cardView.willYouAttend") : t("cardView.preferences", { defaultValue: "Your preferences" })}
                            </p>
                            {rsvpStep === "choose" ? (
                              <>
                                <Input placeholder={t("cardView.yourName")} value={rsvpForm.name} onChange={(e) => setRsvpForm((f) => ({ ...f, name: e.target.value }))} className="h-10 text-sm" />
                                <Input placeholder={t("cardView.emailOptional")} type="email" value={rsvpForm.email} onChange={(e) => setRsvpForm((f) => ({ ...f, email: e.target.value }))} className="h-10 text-sm" />
                                {(collection as any).rsvp_plus_one && (
                                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                    <input type="checkbox" checked={rsvpForm.plusOne} onChange={(e) => setRsvpForm((f) => ({ ...f, plusOne: e.target.checked }))} className="rounded border-border" />
                                    <UserPlus size={14} className="text-muted-foreground" />
                                    {t("cardView.bringingPlusOne")}
                                  </label>
                                )}
                                {rsvpForm.plusOne && (
                                  <Input placeholder={t("cardView.guestName")} value={rsvpForm.plusOneName} onChange={(e) => setRsvpForm((f) => ({ ...f, plusOneName: e.target.value }))} className="h-10 text-sm" />
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    className="flex-1 rounded-full h-11 gap-2"
                                    disabled={!rsvpForm.name.trim() || submittingRsvp}
                                    onClick={() => {
                                      if (rsvpQuestions.length > 0) {
                                        setRsvpStep("preferences");
                                        return;
                                      }

                                      void (async () => {
                                        setSubmittingRsvp(true);
                                        const { error } = await createRsvp({ collection_id: collection.id, guest_name: rsvpForm.name.trim(), guest_email: rsvpForm.email.trim() || undefined, response: rsvpYesResponse, plus_one: rsvpForm.plusOne, plus_one_name: rsvpForm.plusOneName.trim() || undefined, rsvp_answers: {} });
                                        setSubmittingRsvp(false);
                                        if (!error) { setExistingRsvp({ response: rsvpYesResponse }); setRsvpSubmitted(true); setRsvpCelebrationResponse(rsvpYesResponse as "yes" | "waitlist"); }
                                        else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                      })();
                                    }}
                                  >
                                    <Check size={15} /> {rsvpYesLabel}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="rounded-full h-11 px-5"
                                    disabled={!rsvpForm.name.trim() || submittingRsvp}
                                    onClick={async () => {
                                      setSubmittingRsvp(true);
                                      const { error } = await createRsvp({ collection_id: collection.id, guest_name: rsvpForm.name.trim(), guest_email: rsvpForm.email.trim() || undefined, response: "no" });
                                      setSubmittingRsvp(false);
                                      if (!error) { setExistingRsvp({ response: "no" }); setRsvpSubmitted(true); setRsvpCelebrationResponse("no"); }
                                      else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                    }}
                                  >
                                    {t("cardView.cantMakeIt")}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <RsvpPreferenceForm questions={rsvpQuestions} answers={rsvpAnswers} onChange={setRsvpAnswers} />
                                <div className="flex gap-2">
                                  <Button variant="outline" className="rounded-full h-11 px-5" onClick={() => setRsvpStep("choose")}>
                                    {t("common.back", { defaultValue: "Back" })}
                                  </Button>
                                  <Button
                                    className="flex-1 rounded-full h-11 gap-2"
                                    disabled={submittingRsvp}
                                    onClick={async () => {
                                      setSubmittingRsvp(true);
                                      const { error } = await createRsvp({ collection_id: collection.id, guest_name: rsvpForm.name.trim(), guest_email: rsvpForm.email.trim() || undefined, response: rsvpYesResponse, plus_one: rsvpForm.plusOne, plus_one_name: rsvpForm.plusOneName.trim() || undefined, rsvp_answers: rsvpAnswers });
                                      setSubmittingRsvp(false);
                                      if (!error) { setExistingRsvp({ response: rsvpYesResponse }); setRsvpSubmitted(true); setRsvpCelebrationResponse(rsvpYesResponse as "yes" | "waitlist"); }
                                      else toast({ variant: "destructive", title: t("common.error"), description: error.message });
                                    }}
                                  >
                                    <Check size={15} /> {t("cardView.confirmRsvp", { defaultValue: "Confirm RSVP" })}
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Guest action bar */}
                {!isActualOrganizer && (
                  <div className="w-full flex gap-2">
                    {(collection as any).rsvp_enabled && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${
                              existingRsvp?.response === "yes"
                                ? "bg-primary/10 border-primary/30 text-primary"
                                : existingRsvp?.response === "waitlist"
                                ? "bg-accent border-accent-foreground/20 text-accent-foreground"
                                : existingRsvp?.response === "no"
                                ? "bg-destructive/10 border-destructive/30 text-destructive"
                                : existingRsvp?.response === "maybe"
                                ? "bg-accent border-accent-foreground/20 text-accent-foreground"
                                : "bg-card border-border hover:bg-muted/50"
                            }`}
                          >
                            {existingRsvp?.response === "yes" ? (
                              <Check size={18} />
                            ) : existingRsvp?.response === "waitlist" ? (
                              <Users size={18} />
                            ) : existingRsvp?.response === "no" ? (
                              <X size={18} />
                            ) : existingRsvp?.response === "maybe" ? (
                              <HelpCircle size={18} />
                            ) : (
                              <PartyPopper size={18} className="text-foreground" />
                            )}
                            <span className="text-[11px] font-medium">
                              {existingRsvp?.response === "yes"
                                ? t("cardView.attending", { defaultValue: "Attending" })
                                : existingRsvp?.response === "waitlist"
                                ? t("cardView.onWaitlist", { defaultValue: "Waitlisted" })
                                : t("cardView.rsvp")}
                            </span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-[140px]">
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={async () => {
                              if (requiresLogin) {
                                setPendingAction(() => promptYesRsvpFlow);
                                setShowAuthSheet(true);
                                return;
                              }
                              if (rsvpQuestions.length > 0 || !(collection as any).require_login || (collection as any).rsvp_plus_one) {
                                promptYesRsvpFlow();
                                return;
                              }
                              setSubmittingRsvp(true);
                              const guestName = user?.user_metadata?.display_name || user?.email || rsvpForm.name.trim() || "Guest";
                              const guestEmail = user?.email || rsvpForm.email.trim() || undefined;
                              const { error } = await createRsvp({
                                collection_id: collection.id,
                                guest_name: guestName,
                                guest_email: guestEmail,
                                response: rsvpYesResponse,
                              });
                              setSubmittingRsvp(false);
                              if (!error) {
                                setExistingRsvp({ response: rsvpYesResponse });
                                setRsvpSubmitted(true);
                                setRsvpCelebrationResponse(rsvpYesResponse as "yes" | "waitlist");
                              } else {
                                toast({ variant: "destructive", title: t("common.error"), description: error.message });
                              }
                            }}
                          >
                            <Check size={14} className="text-primary" />
                            {capacityReached ? t("cardView.joinWaitlist", { defaultValue: "Join Waitlist" }) : t("cardView.rsvpYes", { defaultValue: "Yes" })}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={async () => {
                              if (requiresLogin) { setShowAuthSheet(true); return; }
                              setSubmittingRsvp(true);
                              const guestName = user?.user_metadata?.display_name || user?.email || rsvpForm.name.trim() || "Guest";
                              const guestEmail = user?.email || rsvpForm.email.trim() || undefined;
                              const { error } = await createRsvp({
                                collection_id: collection.id,
                                guest_name: guestName,
                                guest_email: guestEmail,
                                response: "no",
                              });
                              setSubmittingRsvp(false);
                              if (!error) {
                                setExistingRsvp({ response: "no" });
                                setRsvpSubmitted(true);
                                setRsvpCelebrationResponse("no");
                              } else {
                                toast({ variant: "destructive", title: t("common.error"), description: error.message });
                              }
                            }}
                          >
                            <X size={14} className="text-destructive" />
                            {t("cardView.rsvpNo", { defaultValue: "No" })}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={async () => {
                              if (requiresLogin) { setShowAuthSheet(true); return; }
                              setSubmittingRsvp(true);
                              const guestName = user?.user_metadata?.display_name || user?.email || rsvpForm.name.trim() || "Guest";
                              const guestEmail = user?.email || rsvpForm.email.trim() || undefined;
                              const { error } = await createRsvp({
                                collection_id: collection.id,
                                guest_name: guestName,
                                guest_email: guestEmail,
                                response: "maybe",
                              });
                              setSubmittingRsvp(false);
                              if (!error) {
                                setExistingRsvp({ response: "maybe" });
                                setRsvpSubmitted(true);
                                toast({ title: t("cardView.rsvpMaybeTitle", { defaultValue: "Maybe!" }), description: t("cardView.rsvpMaybeDesc", { defaultValue: "We'll keep a spot for you" }) });
                              } else {
                                toast({ variant: "destructive", title: t("common.error"), description: error.message });
                              }
                            }}
                          >
                            <HelpCircle size={14} className="text-muted-foreground" />
                            {t("cardView.rsvpMaybe", { defaultValue: "Maybe" })}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}

                {/* Organizer's message */}
                {collection.message_to_group && (
                  <div className="w-full bg-muted/30 border border-border rounded-xl p-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-[0.12em]">{t("cardView.noteFromOrganizer")}</p>
                    <p className="text-sm text-foreground italic leading-relaxed">"{collection.message_to_group}"</p>
                  </div>
                )}

                {/* Chip-in progress bar (RSVP mode) */}
                {collection.collection_enabled && (collection as any).collection_goal && (collection as any).collection_goal_amount > 0 && (
                  <div className="w-full bg-card border border-border rounded-2xl px-5 py-4 space-y-2.5">
                    <div className="space-y-0.5 mb-1">
                      <p className="text-sm font-semibold text-foreground">
                        {(collection as any).pool_type === "product"
                          ? t("cardView.chipInProductTitle", { defaultValue: "Let's make this happen for {{name}}", name: collection.recipient_name })
                          : t("cardView.chipInTitle", { defaultValue: "Chip in for a group gift" })}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {(collection as any).pool_type === "product"
                          ? t("cardView.chipInProductHint", { defaultValue: "Chip in any amount — every bit gets this gift closer.", name: collection.recipient_name })
                          : t("cardView.chipInHint", { defaultValue: "If you're planning to gift, contribute towards the group present" })}
                      </p>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        {(collection.show_total_collected || isActualOrganizer) ? (
                          <>
                            <p className="text-2xl font-bold text-foreground tracking-tight leading-none">
                              {currency} {Number(collection.total_collected || 0).toFixed(0)}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                              {t("cardView.of")} {currency} {Number((collection as any).collection_goal_amount).toFixed(0)} {t("cardView.goal")}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm font-medium text-foreground">
                            {t("cardView.chipInActive", { defaultValue: "Chip-in active" })}
                          </p>
                        )}
                      </div>
                      {(collection.show_total_collected || isActualOrganizer) && (
                        <span className="text-lg font-extralight text-muted-foreground">
                          {Math.min(100, Math.round((Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100))}%
                        </span>
                      )}
                    </div>
                    <Progress
                      value={Math.min(100, (Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100)}
                      className="h-2 [&>div]:bg-gradient-primary-animated"
                      animate
                    />
                    {(collection as any).collection_note && (
                      <p className="text-xs text-muted-foreground leading-relaxed italic mt-1">
                        {(collection as any).collection_note}
                      </p>
                    )}
                    {/* Product pools host the Chip-in CTA inside the
                        progress card itself, so the button sits with
                        the goal it's acting on. Money/sign pools keep
                        the button in the below-card / sticky bars. */}
                    {isProductPool && (
                      <Button
                        onClick={() => handleAuthGatedAction(() => setWizardMode("chipIn"))}
                        className="w-full rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base mt-10"
                        size="lg"
                      >
                        {t("cardView.chipIn", { defaultValue: "Chip in" })}
                      </Button>
                    )}
                  </div>
                )}

                {/* Organizer action bar — product pools (Manage removed; Invite moved next to action buttons) */}

                {/* Collected without goal (RSVP mode) */}
                {collection.collection_enabled && !((collection as any).collection_goal && (collection as any).collection_goal_amount > 0) && (collection.show_total_collected || isActualOrganizer) && Number(collection.total_collected || 0) > 0 && (
                  <div className="w-full bg-card border border-border rounded-2xl px-5 py-4">
                    <p className="text-2xl font-bold text-foreground tracking-tight leading-none">
                      {currency} {Number(collection.total_collected || 0).toFixed(0)}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                      {t("cardView.totalCollected", { defaultValue: "Total collected" })}
                    </p>
                    {(collection as any).collection_note && (
                      <p className="text-xs text-muted-foreground leading-relaxed italic mt-2">
                        {(collection as any).collection_note}
                      </p>
                    )}
                  </div>
                )}

                {/* Gift alternatives section — connected card */}
                {collection.collection_enabled && !isProductPool && ((collection as any).show_preferences_in_invite !== false || collection.recipient_email) && (
                  <div className="w-full bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 pt-4 pb-3">
                      <p className="text-sm font-semibold text-foreground">
                        {t("cardView.orGiftSomethingElse", { defaultValue: "Or gift something else?" })}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("cardView.giftAltHint", { defaultValue: "Browse their preferences or pick from their wishlist" })}
                      </p>
                    </div>
                    <div className="divide-y divide-border/50">
                      {(collection as any).show_preferences_in_invite !== false && (
                        <ContributorGiftGuidance collection={collection} />
                      )}
                      {collection.recipient_email && (
                        <GiftAlternativeWishlistRow
                          recipientEmail={collection.recipient_email}
                          recipientName={collection.recipient_name}
                        />
                      )}
                      <GiftAlternativeAIRow collection={collection} />
                    </div>
                  </div>
                )}

                {/* Gift alternatives — standalone (no chip-in), unified connected card */}
                {!collection.collection_enabled && !isProductPool && ((collection as any).show_preferences_in_invite !== false || collection.recipient_email) && (
                  <div className="w-full bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 pt-4 pb-3">
                      <p className="text-sm font-semibold text-foreground">
                        {t("cardView.giftIdeasTitle", { defaultValue: "Gift ideas for " }) + collection.recipient_name}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("cardView.giftIdeasHint", { defaultValue: "Browse their preferences and wishlist" })}
                      </p>
                    </div>
                    <div className="divide-y divide-border/50">
                      {(collection as any).show_preferences_in_invite !== false && (
                        <ContributorGiftGuidance collection={collection} />
                      )}
                      {collection.recipient_email && (
                        <GiftAlternativeWishlistRow
                          recipientEmail={collection.recipient_email}
                          recipientName={collection.recipient_name}
                        />
                      )}
                      <GiftAlternativeAIRow collection={collection} />
                    </div>
                  </div>
                )}

                {/* Wishes & Chip-in */}
                {messages.length > 0 && (
                  <div className="w-full border border-border rounded-2xl py-4 px-4 space-y-3">
                    <div className="space-y-0.5 mb-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground">{t("cardView.whoSigned", { defaultValue: "Who signed" })}</p>
                        <button onClick={() => setShowGroupModal(true)} className="text-[11px] font-medium text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors">
                          {t("cardView.viewAll")}
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {new Set(messages.map(m => m.contributor_email || m.contributor_name)).size} {new Set(messages.map(m => m.contributor_email || m.contributor_name)).size === 1 ? t("cardView.personSigned") : t("cardView.peopleSigned")}
                      </p>
                    </div>
                    <SignersBubbles messages={messages} contributions={contributions} maxVisible={15} />
                  </div>
                )}

                {/* Attendees */}
                {rsvps.length > 0 && (
                  <AttendeesSection rsvps={rsvps} t={t} rsvpQuestions={rsvpQuestions} onViewAll={() => setShowAttendeesModal(true)} />
                )}

              </>
            ) : (
              /* ── NON-INVITATION MODE (RSVP disabled) ── */
              <>

                {/* Promotional narrative header */}
                {(() => {
                  const occasionMap: Record<string, string> = {
                    birthday: "Birthday", farewell: "Farewell", wedding: "Wedding",
                    baby: "Baby", "thank-you": "ThankYou", retirement: "Retirement",
                    "get-well": "GetWell", sympathy: "Sympathy", graduation: "Graduation",
                    teacher: "Teacher", anniversary: "Anniversary", christmas: "Christmas",
                    valentines: "Valentines",
                  };
                  const occasionEmoji: Record<string, string> = {
                    birthday: "🎂", farewell: "👋", wedding: "💍", baby: "👶",
                    "thank-you": "💐", retirement: "🌅", "get-well": "🌷",
                    sympathy: "🕊️", graduation: "🎓", teacher: "🍎",
                    anniversary: "💞", christmas: "🎄", valentines: "💌",
                  };
                  const key = occasionMap[collection.occasion];
                  const name = collection.recipient_name;
                  // Product pools render the organiser's own title and
                  // message verbatim — the templated "A surprise gift
                  // for {name}" framing is wrong when the whole point
                  // is the specific title they wrote. Money / sign-only
                  // pools keep the templated headline + sub copy.
                  const headline = isProductPool
                    ? (collection.title || (name ? t("cardView.wereCelebrating", { name }) : ""))
                    : (name
                        ? (key
                            ? t(`cardView.heading${key}` as any, { name })
                            : t("cardView.wereCelebrating", { name }))
                        : collection.title);
                  const sub = isProductPool
                    ? (collection.message_to_group || "")
                    : (collection.occasion === "birthday" ? t("cardView.occasionBirthday") :
                       collection.occasion === "farewell" ? t("cardView.occasionFarewell") :
                       collection.occasion === "wedding" ? t("cardView.occasionWedding") :
                       collection.occasion === "baby" ? t("cardView.occasionBaby") :
                       t("cardView.occasionGeneric"));
                  const occasionLabel = (collection.occasion || "").replace(/-/g, " ");
                  const eyebrowLabel = isProductPool
                    ? (occasionLabel || t("cardView.groupGiftEyebrow", { defaultValue: "Group gift" }))
                    : (occasionLabel || t("cardView.groupCard", { defaultValue: "Group card" }));
                  return (
                    <div className={`relative w-full ${isProductPool ? 'text-start' : 'text-center overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-secondary/60 via-background to-card'}`}>
                      {/* Decorative corners — money / sign pools only;
                          product pools strip the outer container styling
                          so the left column reads as open content. */}
                      {!isProductPool && (
                        <>
                          <div className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/10 blur-3xl" />
                          <div className="pointer-events-none absolute -bottom-16 -left-12 w-44 h-44 rounded-full bg-primary/5 blur-3xl" />
                        </>
                      )}

                      <div className={`relative ${isProductPool ? 'space-y-3 pt-2 pb-3' : 'space-y-4 px-6 py-8 sm:py-10'}`}>
                        {/* Eyebrow chip */}
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card/80 backdrop-blur border border-border/60 shadow-sm">
                          <span className="text-sm leading-none">{isProductPool ? "🎁" : (occasionEmoji[collection.occasion] || "✨")}</span>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
                            {eyebrowLabel}
                          </span>
                        </div>

                        {/* Headline */}
                        <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-serif leading-[1.1] tracking-tight">
                          {headline}
                        </h2>

                        {/* Subtitle — for product pools the headline is
                            already the user-entered title, so we skip
                            the secondary title line. Money/sign pools
                            still show the title under the templated
                            heading as before. */}
                        {!isProductPool && collection.title && collection.title !== collection.recipient_name && (
                          <p className="text-sm font-medium text-foreground/70 italic">
                            {collection.title}
                          </p>
                        )}

                        {/* Promotional pitch — optional for product
                            pools where it's the organiser's own message
                            (they may have left it blank). */}
                        {sub && (
                          <p className={`text-[15px] text-muted-foreground leading-relaxed max-w-md ${isProductPool ? '' : 'mx-auto'}`}>
                            {sub}
                          </p>
                        )}

                        {/* Deadline pill — product pools show this beside
                            the chip-in section instead of in the hero. */}
                        {!isProductPool && collection.deadline && (
                          <div className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">
                              {t("cardView.closes")} {format(new Date(collection.deadline), "d MMM yyyy")}
                            </span>
                          </div>
                        )}

                      </div>

                      {/* Chip-in panel — product pools present this as a
                          standalone card (the outer container has no bg
                          or border in product mode). */}
                      {isProductPool && collection.collection_enabled && (collection as any).collection_goal && (collection as any).collection_goal_amount > 0 && (
                        <div className="relative rounded-2xl border border-border/60 bg-card/60 backdrop-blur px-6 py-5 text-start space-y-2.5">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div className="space-y-0.5 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                {t("cardView.chipInProductTitle", { defaultValue: "Let's make this happen for {{name}}", name: collection.recipient_name })}
                              </p>
                              <p className="text-[11px] text-muted-foreground leading-snug">
                                {t("cardView.chipInProductHint", { defaultValue: "Chip in any amount — every bit gets this gift closer.", name: collection.recipient_name })}
                              </p>
                            </div>
                            {collection.deadline && (
                              <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 whitespace-nowrap">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-primary">
                                  {t("cardView.closes")} {format(new Date(collection.deadline), "d MMM")}
                                </span>
                              </span>
                            )}
                          </div>
                          {/* Signers row — small avatar circles above the
                              total, showing up to 5 unique signers with a
                              +X counter for the remainder. Clicking opens
                              the stories viewer. */}
                          {messages.length > 0 && (() => {
                            const seen = new Map<string, { key: string; name: string; avatar: string | null }>();
                            for (const msg of messages) {
                              const key = msg.contributor_email || msg.contributor_name;
                              if (!seen.has(key)) {
                                seen.set(key, {
                                  key,
                                  name: msg.contributor_name,
                                  avatar: msg.photo_url || msg.gif_url || null,
                                });
                              }
                            }
                            const all = Array.from(seen.values());
                            const visible = all.slice(0, 5);
                            const remaining = all.length - visible.length;
                            return (
                              <button
                                type="button"
                                onClick={() => { setStoryStartIndex(0); setShowStories(true); }}
                                className="flex items-center -space-x-2 hover:opacity-90 transition-opacity"
                                aria-label={t("cardView.whoSigned", { defaultValue: "Who signed" })}
                              >
                                {visible.map((s) => (
                                  <div key={s.key} className="w-7 h-7 rounded-full ring-2 ring-card overflow-hidden bg-background">
                                    {s.avatar ? (
                                      <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className={`w-full h-full flex items-center justify-center text-[9px] font-semibold ${colorForName(s.name)}`}>
                                        {getInitials(s.name)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {remaining > 0 && (
                                  <div className="w-7 h-7 rounded-full ring-2 ring-card bg-muted flex items-center justify-center text-[10px] font-semibold text-foreground">
                                    +{remaining}
                                  </div>
                                )}
                              </button>
                            );
                          })()}
                          <div className="flex items-end justify-between">
                            <div>
                              {(collection.show_total_collected || isActualOrganizer) ? (
                                <>
                                  <p className="text-2xl font-bold text-foreground tracking-tight leading-none">
                                    {currency} {Number(collection.total_collected || 0).toFixed(0)}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                                    {t("cardView.of")} {currency} {Number((collection as any).collection_goal_amount).toFixed(0)} {t("cardView.goal")}
                                  </p>
                                </>
                              ) : (
                                <p className="text-sm font-medium text-foreground">
                                  {t("cardView.chipInActive", { defaultValue: "Chip-in active" })}
                                </p>
                              )}
                            </div>
                            {(collection.show_total_collected || isActualOrganizer) && (
                              <span className="text-lg font-extralight text-muted-foreground">
                                {Math.min(100, Math.round((Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100))}%
                              </span>
                            )}
                          </div>
                          <Progress
                            value={Math.min(100, (Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100)}
                            className="h-2 [&>div]:bg-gradient-primary-animated"
                            animate
                          />
                          {(collection as any).collection_note && (
                            <p className="text-xs text-muted-foreground leading-relaxed italic mt-1">
                              {(collection as any).collection_note}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-10">
                            <Button
                              onClick={() => handleAuthGatedAction(() => setWizardMode("chipIn"))}
                              className="flex-1 rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow h-12 text-base"
                              size="lg"
                            >
                              {t("cardView.chipIn", { defaultValue: "Chip in" })}
                            </Button>
                            {isActualOrganizer && (
                              <button
                                onClick={() => setShowInviteModal(true)}
                                aria-label={t("cardView.inviteAction")}
                                className="shrink-0 h-12 w-12 rounded-full bg-background border border-border/60 shadow-sm hover:bg-muted/50 transition-colors flex items-center justify-center text-foreground"
                              >
                                <UserPlus size={18} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {collection.message_to_group && !isProductPool && (
                  <div className="w-full bg-muted/30 border border-border rounded-xl p-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-[0.12em]">{t("cardView.noteFromOrganizer")}</p>
                    <p className="text-sm text-foreground italic leading-relaxed">"{collection.message_to_group}"</p>
                  </div>
                )}

                {/* Chip-in progress bar — product pools host this inside
                    the hero card, so this standalone card only renders
                    for money / sign pools. */}
                {!isProductPool && collection.collection_enabled && (collection as any).collection_goal && (collection as any).collection_goal_amount > 0 && (
                  <div className="w-full bg-card border border-border rounded-2xl px-5 py-4 space-y-2.5">
                    <div className="space-y-0.5 mb-1">
                      <p className="text-sm font-semibold text-foreground">
                        {(collection as any).pool_type === "product"
                          ? t("cardView.chipInProductTitle", { defaultValue: "Let's make this happen for {{name}}", name: collection.recipient_name })
                          : t("cardView.chipInTitle", { defaultValue: "Chip in for a group gift" })}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {(collection as any).pool_type === "product"
                          ? t("cardView.chipInProductHint", { defaultValue: "Chip in any amount — every bit gets this gift closer.", name: collection.recipient_name })
                          : t("cardView.chipInHint", { defaultValue: "If you're planning to gift, contribute towards the group present" })}
                      </p>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        {(collection.show_total_collected || isActualOrganizer) ? (
                          <>
                            <p className="text-2xl font-bold text-foreground tracking-tight leading-none">
                              {currency} {Number(collection.total_collected || 0).toFixed(0)}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                              {t("cardView.of")} {currency} {Number((collection as any).collection_goal_amount).toFixed(0)} {t("cardView.goal")}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm font-medium text-foreground">
                            {t("cardView.chipInActive", { defaultValue: "Chip-in active" })}
                          </p>
                        )}
                      </div>
                      {(collection.show_total_collected || isActualOrganizer) && (
                        <span className="text-lg font-extralight text-muted-foreground">
                          {Math.min(100, Math.round((Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100))}%
                        </span>
                      )}
                    </div>
                    <Progress
                      value={Math.min(100, (Number(collection.total_collected || 0) / Number((collection as any).collection_goal_amount)) * 100)}
                      className="h-2 [&>div]:bg-gradient-primary-animated"
                      animate
                    />
                    {(collection as any).collection_note && (
                      <p className="text-xs text-muted-foreground leading-relaxed italic mt-1">
                        {(collection as any).collection_note}
                      </p>
                    )}
                  </div>
                )}

                {/* Collected without goal */}
                {collection.collection_enabled && !((collection as any).collection_goal && (collection as any).collection_goal_amount > 0) && (collection.show_total_collected || isActualOrganizer) && Number(collection.total_collected || 0) > 0 && (
                  <div className="w-full bg-card border border-border rounded-2xl px-5 py-4">
                    <p className="text-2xl font-bold text-foreground tracking-tight leading-none">
                      {currency} {Number(collection.total_collected || 0).toFixed(0)}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1">
                      {t("cardView.totalCollected", { defaultValue: "Total collected" })}
                    </p>
                    {(collection as any).collection_note && (
                      <p className="text-xs text-muted-foreground leading-relaxed italic mt-2">
                        {(collection as any).collection_note}
                      </p>
                    )}
                  </div>
                )}

                {/* Gift alternatives section — connected card */}
                {collection.collection_enabled && !isProductPool && ((collection as any).show_preferences_in_invite !== false || collection.recipient_email) && (
                  <div className="w-full bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 pt-4 pb-3">
                      <p className="text-sm font-semibold text-foreground">
                        {t("cardView.orGiftSomethingElse", { defaultValue: "Or gift something else?" })}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("cardView.giftAltHint", { defaultValue: "Browse their preferences or pick from their wishlist" })}
                      </p>
                    </div>
                    <div className="divide-y divide-border/50">
                      {(collection as any).show_preferences_in_invite !== false && (
                        <ContributorGiftGuidance collection={collection} />
                      )}
                      {collection.recipient_email && (
                        <GiftAlternativeWishlistRow
                          recipientEmail={collection.recipient_email}
                          recipientName={collection.recipient_name}
                        />
                      )}
                      <GiftAlternativeAIRow collection={collection} />
                    </div>
                  </div>
                )}

                {!collection.collection_enabled && !isProductPool && ((collection as any).show_preferences_in_invite !== false || collection.recipient_email) && (
                  <div className="w-full bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 pt-4 pb-3">
                      <p className="text-sm font-semibold text-foreground">
                        {t("cardView.giftIdeasTitle", { defaultValue: "Gift ideas for " }) + collection.recipient_name}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("cardView.giftIdeasHint", { defaultValue: "Browse their preferences and wishlist" })}
                      </p>
                    </div>
                    <div className="divide-y divide-border/50">
                      {(collection as any).show_preferences_in_invite !== false && (
                        <ContributorGiftGuidance collection={collection} />
                      )}
                      {collection.recipient_email && (
                        <GiftAlternativeWishlistRow
                          recipientEmail={collection.recipient_email}
                          recipientName={collection.recipient_name}
                        />
                      )}
                      <GiftAlternativeAIRow collection={collection} />
                    </div>
                  </div>
                )}

                {/* Social proof — hidden for product pools where the
                    hero + product card are meant to be the single focal
                    point of the guest view. */}
                {!isProductPool && messages.length > 0 && (
                  <div className="w-full border border-border rounded-2xl py-4 px-4 space-y-3">
                    <div className="space-y-0.5 mb-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground">{t("cardView.whoSigned", { defaultValue: "Who signed" })}</p>
                        <button onClick={() => setShowGroupModal(true)} className="text-[11px] font-medium text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors">
                          {t("cardView.viewAll")}
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {new Set(messages.map(m => m.contributor_email || m.contributor_name)).size} {new Set(messages.map(m => m.contributor_email || m.contributor_name)).size === 1 ? t("cardView.personSigned") : t("cardView.peopleSigned")}
                      </p>
                    </div>
                    <SignersBubbles messages={messages} contributions={contributions} maxVisible={15} />
                  </div>
                )}

                {/* Attendees */}
                {rsvps.length > 0 && (
                  <AttendeesSection rsvps={rsvps} t={t} rsvpQuestions={rsvpQuestions} onViewAll={() => setShowAttendeesModal(true)} />
                )}

              </>
            )}
          </div>
        </div>
      </div>


      {/* Stories viewer */}
      <AnimatePresence>
        {showStories && (
          <StoriesViewer
            templateUrl={templateUrl}
            title={collection.title}
            messages={messages}
            contributions={contributions}
            onClose={() => { setShowStories(false); }}
            startIndex={storyStartIndex}
            musicId={(collection as any).music_id || null}
            onAction={collection.collection_enabled
              ? (a) => { setShowStories(false); setWizardMode(a); }
              : () => { setShowStories(false); setWizardMode("sign"); }
            }
            onStorySeen={(contributorKey) => {
              setSeenStoryKeys((prev) => {
                if (prev.has(contributorKey)) return prev;
                const next = new Set(prev);
                next.add(contributorKey);
                return next;
              });
              if (user && collection) {
                supabase.from("story_views").upsert(
                  { user_id: user.id, collection_id: collection.id, contributor_key: contributorKey, viewed_at: new Date().toISOString() },
                  { onConflict: "user_id,collection_id,contributor_key" }
                ).then(() => {});
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Contribute Wizard */}
      <AnimatePresence>
        {wizardMode && (
          <ContributeWizard
            collection={collection}
            templateUrl={templateUrl}
            messages={messages}
            chipIn={wizardMode === "chipIn"}
            onClose={() => setWizardMode(null)}
            onSigned={() => setWizardMode(null)}
          />
        )}
      </AnimatePresence>

      {/* Auth bottom sheet */}
      <AnimatePresence>
        {showAuthSheet && (
          <AuthBottomSheet
            onClose={() => { setShowAuthSheet(false); setPendingAction(null); }}
            onAuthenticated={() => {
              setShowAuthSheet(false);
              if (pendingAction) {
                pendingAction();
                setPendingAction(null);
              }
            }}
          />
        )}
      </AnimatePresence>



      {/* Your Group modal */}
      <AnimatePresence>
        {showGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => { setShowGroupModal(false); }}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] shadow-2xl flex flex-col relative overflow-hidden"
            >
              {/* Fixed Header */}
              <div className="flex items-center justify-center py-5 relative shrink-0 border-b border-border">
                <h2 className="text-lg font-bold text-foreground font-serif">{t("cardView.whoSigned", { defaultValue: "Who signed" })}</h2>
                {collection.rsvp_enabled && (
                  <p className="text-[10px] text-muted-foreground absolute bottom-1.5 left-1/2 -translate-x-1/2">
                    {rsvps.filter((r: any) => r.response === "yes").length} attending · {rsvps.filter((r: any) => r.response === "no").length} declined
                  </p>
                )}
                <button
                  onClick={() => { setShowGroupModal(false); }}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground absolute top-4 end-4"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto">

                {/* Stats bar */}
                <div className="flex items-center justify-around px-5 py-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{new Set(messages.map(m => m.contributor_email || m.contributor_name)).size}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.signed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{contributions.filter(c => c.status === "completed").length}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.contributed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">
                      {currency} {contributions.filter(c => c.status === "completed").reduce((sum, c) => sum + Number(c.amount), 0).toFixed(0)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.total")}</p>
                  </div>
                </div>

                {/* Toolbar */}
                <div className="px-5 pb-3 space-y-3">
                  {/* Search + Filter row */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 border border-border rounded-lg px-2.5 py-1.5 bg-muted/30">
                      <Search size={14} className="text-muted-foreground shrink-0" />
                      <input
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        placeholder={t("cardView.searchByName")}
                        className="bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground w-full"
                      />
                    </div>
                  </div>



                </div>

                {/* Members list */}
                <div className="px-5 pb-5">
                  {(() => {
                    const contributionMap = new Map<string, Tables<"contributions">>();
                    contributions.forEach(c => {
                      const existing = contributionMap.get(c.contributor_name);
                      if (!existing || c.status === "completed") {
                        contributionMap.set(c.contributor_name, c);
                      }
                    });

                    type MemberItem = {
                      id: string;
                      name: string;
                      email: string | null;
                      signed: boolean;
                      signedDate: string | null;
                      contributed: boolean;
                      amount: number;
                      contributedDate: string | null;
                      messagePreview: string | null;
                      rsvpStatus: string | null;
                    };

                    const rsvpMap = new Map<string, string>();
                    rsvps.forEach((r: any) => {
                      if (r.guest_email) rsvpMap.set(r.guest_email.toLowerCase(), r.response);
                      if (r.guest_name) rsvpMap.set(r.guest_name.toLowerCase(), r.response);
                    });

                    const membersMap = new Map<string, MemberItem>();

                    messages.forEach(m => {
                      const contrib = contributionMap.get(m.contributor_name);
                      membersMap.set(m.contributor_name, {
                        id: m.id,
                        name: m.contributor_name,
                        email: m.contributor_email,
                        signed: true,
                        signedDate: m.created_at,
                        contributed: contrib?.status === "completed",
                        amount: contrib?.status === "completed" ? Number(contrib.amount) : 0,
                        contributedDate: contrib?.created_at || null,
                        messagePreview: m.message_text?.slice(0, 50) || null,
                        rsvpStatus: rsvpMap.get(m.contributor_email?.toLowerCase() || "") || rsvpMap.get(m.contributor_name.toLowerCase()) || null,
                      });
                    });

                    contributions.forEach(c => {
                      if (!membersMap.has(c.contributor_name)) {
                        membersMap.set(c.contributor_name, {
                          id: c.id,
                          name: c.contributor_name,
                          email: c.contributor_email,
                          signed: false,
                          signedDate: null,
                          contributed: c.status === "completed",
                          amount: c.status === "completed" ? Number(c.amount) : 0,
                          contributedDate: c.created_at,
                          messagePreview: null,
                          rsvpStatus: rsvpMap.get(c.contributor_email?.toLowerCase() || "") || rsvpMap.get(c.contributor_name.toLowerCase()) || null,
                        });
                      }
                    });

                    // Add RSVP-only attendees (people who RSVP'd but didn't sign or contribute)
                    rsvps.forEach((r: any) => {
                      const nameKey = r.guest_name;
                      if (!membersMap.has(nameKey)) {
                        membersMap.set(nameKey, {
                          id: r.id,
                          name: r.guest_name,
                          email: r.guest_email || null,
                          signed: false,
                          signedDate: null,
                          contributed: false,
                          amount: 0,
                          contributedDate: null,
                          messagePreview: null,
                          rsvpStatus: r.response,
                        });
                      }
                    });

                    let members = Array.from(membersMap.values());
                    if (groupSearch) {
                      const q = groupSearch.toLowerCase();
                      members = members.filter(m =>
                        m.name.toLowerCase().includes(q) ||
                        (m.email && m.email.toLowerCase().includes(q))
                      );
                    }

                    if (members.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground text-sm">
                          {messages.length === 0 && contributions.length === 0
                            ? t("cardView.noMembersYet")
                            : t("cardView.noResults")}
                        </div>
                      );
                    }

                    const selectedMemberMessage = selectedGroupMemberId
                      ? messages.find(msg => msg.id === selectedGroupMemberId)
                      : null;
                    const selectedMemberContribution = selectedMemberMessage
                      ? contributions.find(c => c.contributor_email === selectedMemberMessage.contributor_email && c.status === "completed")
                      : undefined;

                    return (
                      <>
                        <div className="divide-y divide-border">
                          {members.map((m) => {
                            const message = messages.find(msg => msg.id === m.id);

                            return (
                              <button
                                key={m.id}
                                className="flex items-center gap-3 py-3 w-full text-start hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => {
                                  if (message) setSelectedGroupMemberId(message.id);
                                }}
                              >
                                <div className="relative shrink-0">
                                  {message ? (
                                    <BubbleMedia message={message} size="sm" />
                                  ) : (
                                    <div className={`w-14 h-14 rounded-full ring-2 ring-background flex items-center justify-center font-medium text-xs ${colorForName(m.name)}`}>
                                      {getInitials(m.name)}
                                    </div>
                                  )}
                                  {m.contributed && (
                                    <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-amber-400 text-amber-900 flex items-center justify-center text-[10px] font-bold ring-2 ring-background shadow-sm">
                                      $
                                    </span>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                                    {m.rsvpStatus && (
                                      <span className={`shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                                        m.rsvpStatus === "yes" ? "bg-emerald-100 text-emerald-700" :
                                        m.rsvpStatus === "no" ? "bg-red-100 text-red-600" :
                                        m.rsvpStatus === "waitlist" ? "bg-amber-100 text-amber-700" :
                                        "bg-muted text-muted-foreground"
                                      }`}>
                                        {m.rsvpStatus === "yes" ? "Going" : m.rsvpStatus === "no" ? "Declined" : m.rsvpStatus === "waitlist" ? "Waitlist" : m.rsvpStatus}
                                      </span>
                                    )}
                                  </div>
                                  {m.messagePreview && (
                                    <p className="text-xs text-muted-foreground/70 truncate italic mt-0.5">
                                      "{m.messagePreview}{m.messagePreview.length >= 50 ? "..." : ""}"
                                    </p>
                                  )}
                                </div>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {m.signedDate
                                    ? new Date(m.signedDate).toLocaleDateString()
                                    : m.contributedDate
                                      ? new Date(m.contributedDate).toLocaleDateString()
                                      : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        <AnimatePresence>
                          {selectedMemberMessage && (
                            <SignerDetail
                              message={selectedMemberMessage}
                              contribution={selectedMemberContribution}
                              onClose={() => setSelectedGroupMemberId(null)}
                            />
                          )}
                        </AnimatePresence>
                      </>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite modal */}
      <AnimatePresence>
        {showInviteModal && collection && (
          <InviteModal
            collectionId={collection.id}
            collectionTitle={collection.title}
            shareSlug={collection.share_slug || undefined}
            organizerName={user?.user_metadata?.display_name}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Attendees modal — invited list with RSVP status */}
      <AnimatePresence>
        {showAttendeesModal && collection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowAttendeesModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] shadow-2xl flex flex-col relative overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-center py-5 relative shrink-0 border-b border-border">
                <h2 className="text-lg font-bold text-foreground font-serif">
                  {t("cardView.attendees", { defaultValue: "Attendees" })}
                </h2>
                <button
                  onClick={() => setShowAttendeesModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground absolute top-4 end-4"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {(() => {
                  // Build merged list: every invited guest + RSVP-only guests
                  const rsvpByEmail = new Map<string, any>();
                  const rsvpByName = new Map<string, any>();
                  rsvps.forEach((r: any) => {
                    if (r.guest_email) rsvpByEmail.set(r.guest_email.toLowerCase(), r);
                    if (r.guest_name) rsvpByName.set(r.guest_name.toLowerCase(), r);
                  });

                  type Row = { key: string; name: string | null; email: string | null; status: "yes" | "no" | "waitlist" | "pending"; plusOne: boolean; plusOneName: string | null };
                  const rows: Row[] = [];
                  const seenEmails = new Set<string>();

                  invitedGuests.forEach((g) => {
                    const emailLower = g.email.toLowerCase();
                    seenEmails.add(emailLower);
                    const r = rsvpByEmail.get(emailLower);
                    rows.push({
                      key: `inv-${g.id}`,
                      name: r?.guest_name || null,
                      email: g.email,
                      status: (r?.response as Row["status"]) || "pending",
                      plusOne: !!r?.plus_one,
                      plusOneName: r?.plus_one_name || null,
                    });
                  });

                  // RSVPs from people who weren't on the invited list (e.g. organizer-added)
                  rsvps.forEach((r: any) => {
                    const emailLower = r.guest_email?.toLowerCase();
                    if (emailLower && seenEmails.has(emailLower)) return;
                    rows.push({
                      key: `rsvp-${r.id}`,
                      name: r.guest_name || null,
                      email: r.guest_email || null,
                      status: (r.response as Row["status"]) || "pending",
                      plusOne: !!r.plus_one,
                      plusOneName: r.plus_one_name || null,
                    });
                  });

                  const yesCount = rows.filter((r) => r.status === "yes").length;
                  const noCount = rows.filter((r) => r.status === "no").length;
                  const waitlistCount = rows.filter((r) => r.status === "waitlist").length;
                  const pendingCount = rows.filter((r) => r.status === "pending").length;

                  const statusLabel = (s: Row["status"]) => {
                    switch (s) {
                      case "yes": return t("cardView.attendeeGoing", { defaultValue: "Going" });
                      case "no": return t("cardView.attendeeDeclined", { defaultValue: "Declined" });
                      case "waitlist": return t("cardView.attendeeWaitlist", { defaultValue: "Waitlist" });
                      default: return t("cardView.attendeePending", { defaultValue: "Pending" });
                    }
                  };
                  const statusClass = (s: Row["status"]) => {
                    switch (s) {
                      case "yes": return "bg-emerald-100 text-emerald-700";
                      case "no": return "bg-red-100 text-red-600";
                      case "waitlist": return "bg-amber-100 text-amber-700";
                      default: return "bg-muted text-muted-foreground";
                    }
                  };

                  if (rows.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        {t("cardView.noInvitedYet", { defaultValue: "No one has been invited yet." })}
                      </p>
                    );
                  }

                  return (
                    <>
                      {/* Stats bar */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="rounded-lg bg-emerald-50 py-2">
                          <p className="text-base font-semibold text-emerald-700">{yesCount}</p>
                          <p className="text-[10px] text-emerald-700/80 uppercase tracking-wider">{t("cardView.attendeeGoing", { defaultValue: "Going" })}</p>
                        </div>
                        <div className="rounded-lg bg-red-50 py-2">
                          <p className="text-base font-semibold text-red-600">{noCount}</p>
                          <p className="text-[10px] text-red-600/80 uppercase tracking-wider">{t("cardView.attendeeDeclined", { defaultValue: "Declined" })}</p>
                        </div>
                        <div className="rounded-lg bg-amber-50 py-2">
                          <p className="text-base font-semibold text-amber-700">{waitlistCount}</p>
                          <p className="text-[10px] text-amber-700/80 uppercase tracking-wider">{t("cardView.attendeeWaitlist", { defaultValue: "Waitlist" })}</p>
                        </div>
                        <div className="rounded-lg bg-muted py-2">
                          <p className="text-base font-semibold text-muted-foreground">{pendingCount}</p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t("cardView.attendeePending", { defaultValue: "Pending" })}</p>
                        </div>
                      </div>

                      {/* List */}
                      <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                        {rows.map((row) => {
                          const initials = (row.name || row.email || "?")
                            .split(/[\s@]/)
                            .filter(Boolean)
                            .map((w) => w[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2);
                          return (
                            <li key={row.key} className="flex items-center gap-3 px-3 py-2.5">
                              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-[11px] font-medium text-foreground shrink-0">
                                {initials}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {row.name || row.email || t("cardView.unknownGuest", { defaultValue: "Guest" })}
                                </p>
                                {row.email && row.name && (
                                  <p className="text-[11px] text-muted-foreground truncate">{row.email}</p>
                                )}
                                {row.plusOne && (
                                  <p className="text-[11px] text-muted-foreground">
                                    {t("cardView.plusOneLabel", { defaultValue: "+1" })}
                                    {row.plusOneName ? ` · ${row.plusOneName}` : ""}
                                  </p>
                                )}
                              </div>
                              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full shrink-0 ${statusClass(row.status)}`}>
                                {statusLabel(row.status)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* RSVP Celebration Overlay */}
      <AnimatePresence>
        {rsvpCelebrationResponse && (
          <RsvpCelebration
            response={rsvpCelebrationResponse}
            recipientName={collection?.recipient_name}
            onDone={() => setRsvpCelebrationResponse(null)}
          />
        )}
      </AnimatePresence>

    </div>
    </div>
  );
};


function ContributorGiftGuidance({ collection }: { collection: Tables<"collections"> }) {
  const [expanded, setExpanded] = useState(false);
  const [registryNames, setRegistryNames] = useState<Record<string, string>>({});
  const [openRegistryId, setOpenRegistryId] = useState<string | null>(null);
  const { t } = useTranslation();
  const col = collection as any;
  const hasPrefs =
    col.preferences_wants ||
    col.preferences_has ||
    col.preferences_dislikes ||
    col.preferences_style ||
    (col.preferences_registries && col.preferences_registries.length > 0 && col.preferences_registries.some((r: string) => r.trim()));

  const isValidUrl = (str: string) => {
    try { new URL(str); return true; } catch { return false; }
  };
  const getDomain = (url: string) => {
    try { return new URL(url).hostname.replace("www.", ""); } catch { return url; }
  };
  const splitToTags = (text: string) =>
    text.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);

  // Fetch wishlist names for internal registry URLs
  useEffect(() => {
    const registries: string[] = col.preferences_registries || [];
    const internalIds: string[] = [];

    registries.forEach((url: string) => {
      if (!url.trim() || !isValidUrl(url)) return;
      try {
        const parsed = new URL(url);
        const match = parsed.pathname.match(/\/wishlists\/([a-f0-9-]{36})/);
        if (match) internalIds.push(match[1]);
      } catch { /* ignore */ }
    });

    if (internalIds.length === 0) return;

    (async () => {
      const { data } = await supabase
        .from("wishlists")
        .select("id, title, type")
        .in("id", internalIds);

      if (data) {
        const map: Record<string, string> = {};
        data.forEach((w) => {
          map[w.id] = w.title;
        });
        setRegistryNames(map);
      }
    })();
  }, [col.preferences_registries]);

  if (!hasPrefs) return null;

  const recipientName = collection.recipient_name || t("cardView.them");

  const getRegistryLabel = (url: string): string => {
    try {
      const parsed = new URL(url);
      const match = parsed.pathname.match(/\/wishlists\/([a-f0-9-]{36})/);
      if (match && registryNames[match[1]]) return registryNames[match[1]];
    } catch { /* ignore */ }
    return getDomain(url);
  };

  const sections: { icon: React.ReactNode; label: string; content: React.ReactNode }[] = [];

  if (col.preferences_wants) {
    sections.push({
      icon: <Heart size={16} className="text-muted-foreground shrink-0 mt-0.5" />,
      label: t("cardView.whatTheyWouldLove", { name: recipientName }),
      content: (
        <div className="flex flex-wrap gap-1.5">
          {splitToTags(col.preferences_wants).map((item: string, i: number) => (
            <span key={i} className="text-xs text-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
          ))}
        </div>
      ),
    });
  }
  if (col.preferences_has) {
    sections.push({
      icon: <PackageCheck size={16} className="text-muted-foreground shrink-0 mt-0.5" />,
      label: t("cardView.alreadyHas"),
      content: (
        <div className="flex flex-wrap gap-1.5">
          {splitToTags(col.preferences_has).map((item: string, i: number) => (
            <span key={i} className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
          ))}
        </div>
      ),
    });
  }
  if (col.preferences_dislikes) {
    sections.push({
      icon: <ThumbsDown size={16} className="text-muted-foreground shrink-0 mt-0.5" />,
      label: t("cardView.pleaseAvoid"),
      content: (
        <div className="flex flex-wrap gap-1.5">
          {splitToTags(col.preferences_dislikes).map((item: string, i: number) => (
            <span key={i} className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
          ))}
        </div>
      ),
    });
  }
  if (col.preferences_style) {
    sections.push({
      icon: <Palette size={16} className="text-muted-foreground shrink-0 mt-0.5" />,
      label: t("cardView.styleAndTaste"),
      content: (
        <div className="flex flex-wrap gap-1.5">
          {splitToTags(col.preferences_style).map((item: string, i: number) => (
            <span key={i} className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{item}</span>
          ))}
        </div>
      ),
    });
  }
  // Extract internal wishlist IDs for click-to-open
  const getWishlistIdFromUrl = (url: string): string | null => {
    try {
      const parsed = new URL(url);
      const match = parsed.pathname.match(/\/wishlists\/([a-f0-9-]{36})/);
      return match ? match[1] : null;
    } catch { return null; }
  };

  if (col.preferences_registries?.some((r: string) => r.trim() && isValidUrl(r))) {
    const links = col.preferences_registries.filter((r: string) => r.trim() && isValidUrl(r));
    sections.push({
      icon: <ExternalLink size={16} className="text-muted-foreground shrink-0 mt-0.5" />,
      label: t("cardView.registries"),
      content: (
        <div className="flex flex-wrap gap-1.5">
          {links.map((r: string, i: number) => {
            const wishlistId = getWishlistIdFromUrl(r);
            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  if (wishlistId) {
                    setOpenRegistryId(wishlistId);
                  } else {
                    window.open(r, "_blank", "noopener,noreferrer");
                  }
                }}
                className="text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
              >
                <ExternalLink size={10} />
                {getRegistryLabel(r)}
              </button>
            );
          })}
        </div>
      ),
    });
  }


  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Gift size={16} className="text-muted-foreground shrink-0" />
          <span className="text-sm text-foreground">{t("cardView.viewGiftPreferences", { defaultValue: "View gift preferences" })}</span>
        </div>
        <ChevronRight size={16} className="text-muted-foreground" />
      </button>

      {/* Bottom sheet for Gift Tips */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-end justify-center"
            onClick={() => setExpanded(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl w-full max-w-lg max-h-[85vh] shadow-2xl flex flex-col "
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h2 className="text-base font-bold text-foreground font-serif">
                  {recipientName}'s Gift Preferences
                </h2>
                <button
                  onClick={() => setExpanded(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-border">
                  {sections.map((section, i) => (
                    <div key={i} className="px-5 py-3 flex gap-3">
                      {section.icon}
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">{section.label}</p>
                        {section.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* Bottom sheet for wishlist/registry */}
      {openRegistryId && (
        <WishlistBottomSheet
          wishlist={{ id: openRegistryId, title: registryNames[openRegistryId] || "Wishlist", type: "wishlist" as const, user_id: "", created_at: "", updated_at: "", cover_url: null, description: null, event_date: null }}
          onClose={() => setOpenRegistryId(null)}
        />
      )}
    </>
  );
}

// --- Gift Alternative AI Row ---
function GiftAlternativeAIRow({ collection }: { collection: Tables<"collections"> }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { t } = useTranslation();
  const col = collection as any;
  const recipientName = collection.recipient_name || t("cardView.them");
  const splitToTags = (text: string) =>
    text.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
  const wants = col.preferences_wants ? splitToTags(col.preferences_wants) : [];
  const has = col.preferences_has ? splitToTags(col.preferences_has) : [];
  const dislikesList = col.preferences_dislikes ? splitToTags(col.preferences_dislikes) : [];
  const styleList = col.preferences_style ? splitToTags(col.preferences_style) : [];

  return (
    <>
      <button
        type="button"
        onClick={() => setShowSuggestions(true)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-primary shrink-0" />
          <span className="text-sm text-foreground">Get AI gift ideas</span>
        </div>
        <ChevronRight size={16} className="text-muted-foreground" />
      </button>

      <AnimatePresence>
        {showSuggestions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 flex items-end justify-center"
            onClick={() => setShowSuggestions(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl w-full max-w-lg max-h-[85vh] shadow-2xl flex flex-col "
            >
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h2 className="text-base font-bold text-foreground font-serif">AI Gift Ideas</h2>
                <button
                  onClick={() => setShowSuggestions(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <GiftSuggestions
                  recipientName={recipientName}
                  occasion={collection.occasion}
                  wants={wants}
                  has={has}
                  dislikes={dislikesList}
                  style={styleList}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// --- Gift Alternative Wishlist Row ---
function GiftAlternativeWishlistRow({ recipientEmail, recipientName }: { recipientEmail: string; recipientName: string }) {
  const [showWishlist, setShowWishlist] = useState(false);
  const { t } = useTranslation();
  const [hasWishlists, setHasWishlists] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { getGiftDataByRecipientEmail } = await import("@/services/wishlists");
      const { data } = await getGiftDataByRecipientEmail(recipientEmail);
      if (!active) return;
      const lists = data?.wishlists?.filter((w: any) => w.type === "wishlist" || w.type === "registry") || [];
      setHasWishlists(lists.length > 0);
    })();
    return () => { active = false; };
  }, [recipientEmail]);

  if (hasWishlists === false) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowWishlist(true)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ShoppingBag size={16} className="text-muted-foreground shrink-0" />
          <span className="text-sm text-foreground">{t("cardView.seeWishList", { defaultValue: "See the wish list" })}</span>
        </div>
        <ChevronRight size={16} className="text-muted-foreground" />
      </button>

      <AnimatePresence>
        {showWishlist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-end justify-center"
            onClick={() => setShowWishlist(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl w-full max-w-lg max-h-[85vh] shadow-2xl flex flex-col overflow-hidden "
            >
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <h3 className="text-base font-bold text-foreground font-serif">{recipientName}'s Wishlists</h3>
                <button
                  onClick={() => setShowWishlist(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 px-5 py-4">
                <RecipientWishlistsInline
                  recipientEmail={recipientEmail}
                  recipientName={recipientName}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// --- Attendees Section ---
function AttendeesSection({ rsvps, t, rsvpQuestions, onViewAll }: { rsvps: any[]; t: (key: string, opts?: any) => string; rsvpQuestions?: RsvpQuestion[]; onViewAll?: () => void }) {
  const yesCount = rsvps.filter((r: any) => r.response === "yes").length;
  const noCount = rsvps.filter((r: any) => r.response === "no").length;
  const bgColors = ["#f3e0e4", "#dde8f0", "#f0eadc", "#e4dce8", "#dce8e2", "#e8e0d8"];
  const textColors = [
    "hsl(var(--primary))", "hsl(210 30% 40%)", "hsl(36 30% 40%)",
    "hsl(270 20% 40%)", "hsl(150 20% 35%)", "hsl(25 20% 40%)",
  ];

  return (
    <div
      className="w-full bg-card border border-border rounded-2xl p-4 space-y-3 cursor-pointer hover:border-primary/30 transition-colors"
      onClick={onViewAll}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">
          {t("cardView.attendees", { defaultValue: "Attendees" })}
        </p>
        <span className="text-xs text-muted-foreground font-light">
          {yesCount} {t("cardView.attending", { defaultValue: "attending" })}
          {noCount > 0 && ` · ${noCount} ${t("cardView.declined", { defaultValue: "declined" })}`}
        </span>
      </div>
      <div className="flex items-center">
        <div className="flex items-center">
          {rsvps.slice(0, 8).map((r: any, i: number) => {
            const initials = (r.guest_name || "?")
              .split(" ")
              .map((w: string) => w[0])
              .join("")
              .toUpperCase()
              .slice(0, 2);
            const avatarNode = (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-normal tracking-wide"
                style={{
                  backgroundColor: bgColors[i % bgColors.length],
                  color: textColors[i % textColors.length],
                  boxShadow: "0 0 0 2.5px hsl(var(--background))",
                }}
              >
                {initials}
              </div>
            );

            return (
              <Tooltip key={r.id || i}>
              <TooltipTrigger asChild>
              <div                
                className="relative"
                style={{ zIndex: 8 - i, marginLeft: i === 0 ? 0 : -10 }}
              >
                {avatarNode}
                <div
                  className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: r.response === "yes" ? "#34a853" : "#9e9e9e",
                    boxShadow: "0 0 0 2px hsl(var(--background))",
                  }}
                >
                  {r.response === "yes" ? (
                    <Check size={8} className="text-white" strokeWidth={3} />
                  ) : (
                    <X size={8} className="text-white" strokeWidth={3} />
                  )}
                </div>
              </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-start">
                <p className="text-xs font-semibold">{r.guest_name}</p>
                <p className="text-[10px] text-muted-foreground capitalize">{r.response}</p>
              </TooltipContent>
              </Tooltip>
            );
          })}
          {rsvps.length > 8 && (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-normal text-muted-foreground"
              style={{
                backgroundColor: "hsl(var(--muted))",
                boxShadow: "0 0 0 2.5px hsl(var(--background))",
                zIndex: 0,
                marginLeft: -10,
              }}
            >
              +{rsvps.length - 8}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground font-light leading-relaxed">
        {rsvps.slice(0, 5).map((r: any) => r.guest_name || "Guest").join(", ")}
        {rsvps.length > 5 && ` +${rsvps.length - 5}`}
      </p>
      {/* Preference summary */}
      {rsvpQuestions && rsvpQuestions.length > 0 && (() => {
        const yesRsvps = rsvps.filter((r: any) => r.response === "yes");
        const summaries = rsvpQuestions.map((q) => {
          const tally: Record<string, number> = {};
          const freeTexts: string[] = [];
          yesRsvps.forEach((r: any) => {
            const ans = r.rsvp_answers?.[q.id];
            if (!ans) return;
            if (q.type === "text") {
              if (typeof ans === "string" && ans.trim()) freeTexts.push(ans.trim());
            } else if (Array.isArray(ans)) {
              ans.forEach((v: string) => { tally[v] = (tally[v] || 0) + 1; });
            } else if (typeof ans === "string") {
              tally[ans] = (tally[ans] || 0) + 1;
            }
          });
          const hasData = Object.keys(tally).length > 0 || freeTexts.length > 0;
          return { question: q.question, tally, freeTexts, hasData, type: q.type };
        });
        if (!summaries.some((s) => s.hasData)) return null;
        return (
          <Collapsible className="border-t border-border pt-3">
            <CollapsibleTrigger className="flex items-center justify-between w-full group">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                {t("cardView.preferenceSummary", { defaultValue: "Preference summary" })}
              </p>
              <ChevronDown size={14} className="text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2.5 pt-2.5">
              {summaries.filter((s) => s.hasData).map((s, idx) => (
                <div key={idx} className="space-y-1">
                  <p className="text-[11px] font-medium text-foreground">{s.question}</p>
                  {Object.keys(s.tally).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(s.tally).sort((a, b) => b[1] - a[1]).map(([option, count]) => (
                        <span key={option} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-border bg-muted/40 text-foreground">
                          {option} <span className="text-muted-foreground font-medium">×{count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {s.freeTexts.length > 0 && (
                    <div className="space-y-0.5">
                      {s.freeTexts.map((txt, j) => (
                        <p key={j} className="text-[11px] text-muted-foreground italic">"{txt}"</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        );
      })()}
    </div>
  );
}


export default CardView;
